<template>
  <CustomTable
    :columns="tableFields"
    :data="tableData"
    :is-loading="loading"
    :empty-text="getUiText('app-settings-domains-table-empty-domain-list-1')"
    @update-selected-rows="handleSelectDomain($event)"
  >
    <template #cell(domain)="data">
      <div class="information-container tw-flex tw-items-center">
        <img
          v-if="data.value.imgSrc"
          :src="data.value.imgSrc"
          alt="person_random"
        />
        <div class="information tw-flex tw-mr-12 tw-items-center tw-gap-2">
          <span class="name-value">{{ data.value }}</span>
        </div>
      </div>
    </template>

    <template #cell(status)="data">
      <StatusBadge :value="data.value" />
    </template>

    <template #cell(isVerified)="data">
      <div class="tw-items-center tw-inline-flex">
        <div
          :class="isVerified[data.value.isVerifiedDNS].className"
          class="cell-status-domain tw-flex tw-items-center"
        >
          <span>{{ isVerified[data.value.isVerifiedDNS].label }}</span>
        </div>
      </div>
      <div class="tw-items-center tw-inline-flex tw-ml-[5px]">
        <div
          :class="isEmailVerified[data.value.isVerifiedEmail].className"
          class="cell-status-domain tw-flex tw-items-center"
        >
          <span>{{ isEmailVerified[data.value.isVerifiedEmail].label }}</span>
        </div>
      </div>
    </template>

    <template #cell(fromDate)="data">
      <div class="tw-items-center tw-inline-flex">
        <span v-if="data?.item?.startDate" class="domain-value">
          {{ convertUTCTimeToLocalTime(data.item.startDate) }}
        </span>
      </div>
    </template>

    <template #cell(action)="data">
      <CellTableAction
        :id="'domain-table-' + data.item.id"
        :data-cy="'domain-table-' + data.idx"
        :value="data"
        :is-internal="false"
        :actions="optionDomainActions(data)"
        @manage-user="handleManageDomain"
      />
    </template>
  </CustomTable>
</template>

<script lang="ts">
import store from "@/store";
import utilities from "@/utils/utilities";
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";
import { defineComponent } from "vue";
import { actionClickType } from "@/utils/constants";
import CustomTable from "@/components/common/customTable.vue";
import StatusBadge from "@/components/common/setting/statusBadge.vue";
import CellTableAction from "@/components/common/setting/cellTableAction.vue";
import { IDomain } from "@/interfaces/org/domain";

export default defineComponent({
  components: {
    CustomTable,
    StatusBadge,
    CellTableAction,
  },
  props: {
    tableData: {
      type: Array as () => any[],
      default: () => [],
    },
    tableFields: {
      type: Array as () => any[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    "verify-domain",
    "manage-domain",
    "hide-popover",
    "update:hidePopover",
    "delete",
    "select:row",
    "select:all_row",
    "un-verify-domain",
    "un-verify-email",
    "send-verification-email",
  ],
  data() {
    return {
      selected: [] as any[],
      isSelectAll: false,
      isShowPopover: false,
      isVerified: {
        true: { className: "activeStatus", label: "Verified DNS" },
        false: { className: "require", label: "DNS not verified" },
      },
      isEmailVerified: {
        true: { className: "activeStatus", label: "Verified DNS" },
        false: { className: "require", label: "DNS not verified" },
      },
      externalMembersActions: [
        actionClickType.actionVerifyDomain,
        actionClickType.actionManageDomain,
        actionClickType.actionUnVerifyDomain,
        actionClickType.actionUnVerifyEmail,
        actionClickType.actionDeleteDomain,
        actionClickType.actionSendVerificationEmail,
      ],
    };
  },

  computed: {
    languageSelected() {
      return store.state.LanguagePicker.languageSelected;
    },
    defaultUiText() {
      return store.state.LanguagePicker.defaultUiText;
    },
    languageSelectedText() {
      return store.state.LanguagePicker.languageSelectedText;
    },
  },

  watch: {
    tableData() {
      this.selected = this.tableData;
      this.updateLabelCellStatus();
      this.updateLabelCellEmailStatus();
    },
  },

  created() {
    this.selected = this.tableData;
    this.updateLabelCellStatus();
    this.updateLabelCellEmailStatus();
  },

  methods: {
    convertUTCTimeToLocalTime,
    getUiText(id: string, isArray = false) {
      return utilities.getUiText(
        id,
        this.languageSelectedText,
        this.defaultUiText,
        isArray,
      );
    },

    updateLabelCellStatus() {
      const verifyText = this.getUiText(
        "app-component-table-cell-status-dns-verified-1",
      );
      const verifyRequiredText = this.getUiText(
        "app-component-table-cell-status-dns-not-verified-1",
      );

      this.isVerified.true.label = verifyText;
      this.isVerified.false.label = verifyRequiredText;
    },

    updateLabelCellEmailStatus() {
      const verifyText = this.getUiText(
        "app-component-table-cell-status-email-verified-1",
      );
      const verifyRequiredText = this.getUiText(
        "app-component-table-cell-status-email-not-verified-1",
      );

      this.isEmailVerified.true.label = verifyText;
      this.isEmailVerified.false.label = verifyRequiredText;
    },

    handleSelectDomain(domains: number[]) {
      this.$emit("select:row", domains);
    },

    handleManageDomain(domain: IDomain, action: string) {
      switch (action) {
        case actionClickType.actionVerifyDomain:
          this.$emit("verify-domain", domain);
          break;
        case actionClickType.actionManageDomain:
          this.$emit("manage-domain", domain.domainId);
          break;
        case actionClickType.actionUnVerifyDomain:
          this.$emit("un-verify-domain", domain);
          break;
        case actionClickType.actionUnVerifyEmail:
          this.$emit("un-verify-email", domain);
          break;
        case actionClickType.actionDeleteDomain:
          if (domain.domainId) {
            this.$emit("delete", domain.domainId);
          }
          break;
        case actionClickType.actionSendVerificationEmail:
          this.$emit("send-verification-email", domain);
          break;

        default:
      }
    },

    optionDomainActions(data: any) {
      const actions = [...this.externalMembersActions];

      if (
        data.item.isVerified &&
        data.item.isVerified.isVerifiedDNS === false
      ) {
        const idx = actions.indexOf(actionClickType.actionUnVerifyDomain);
        if (idx !== -1) actions.splice(idx, 1);
      }

      if (
        data.item.isVerified &&
        data.item.isVerified.isVerifiedEmail === false
      ) {
        const idx = actions.indexOf(actionClickType.actionUnVerifyEmail);
        if (idx !== -1) actions.splice(idx, 1);
      }

      return actions;
    },
  },
});
</script>

<style lang="scss">
.header-title {
  color: var(--ui-dark-grey, #878787);
  /* labels/label-10_semi */
  font-family: Poppins;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.name-value {
  color: var(--ui-dark, #231f20);
  /* text/text-14_med */
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}

.domain-value {
  color: var(--ui-dark-grey, #878787);
  /* text/text-14_reg */
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%;
}

.cell-status-domain {
  padding: 3px 5px;
  font-family: Poppins;
  font-size: 11px;
  font-weight: 500;
  line-height: 130%;
  border-radius: 4px;
}

.activeStatus {
  background: var(--ui-green-soft, #d9ede2);
  color: #01893d;
}

.require {
  background: var(--ui-orange-soft, #fee5e2);
  color: var(--brand-orange, #f7553c);
}
</style>
